import React, { createContext, useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';
import Cookies from 'js-cookie';
import { apiErrorMessage } from '../utils/extract-api-error';
import { setBrevoUserRole, setBrevoContext } from '../utils/brevo';
import { storeCookiesInParentDomain, removeCookieFromParentDomain } from './guestAgentChatContextProvider';
import { getAgentDashboardLink } from '../lib/utils';
import {
    getLocalStorageItem,
    removeLocalStorageItem,
    setLocalStorageItem,
} from '../utils/browser-storage';
import {
    capturePostHogEvent,
    groupPostHogClient,
    identifyPostHogUser,
    resetPostHog,
} from '../lib/posthog';

// Access token cookie name — shared across all *.leasehub.app subdomains
// (agent_dashboard and sign_dashboard read this same cookie and have no
// login of their own; they rely entirely on this app to set it).
const ACCESS_TOKEN_COOKIE = 'token';

function getSafeRedirectTarget(path, { allowAgentDashboard = false } = {}) {
    if (!path || typeof path !== 'string') return null;
    if (path.startsWith('/') && !path.startsWith('//') && !path.includes('://')) {
        return path;
    }
    if (!allowAgentDashboard) return null;

    try {
        const target = new URL(path);
        const agentDashboard = new URL(getAgentDashboardLink(), window.location.origin);
        if (target.origin === agentDashboard.origin) {
            if (['/401', '/403', '/404', '/500', '/503'].includes(target.pathname)) {
                return null;
            }
            return target.toString();
        }
    } catch (_) {
        return null;
    }
    return null;
}

function isSafeRedirectPath(path) {
    return Boolean(getSafeRedirectTarget(path));
}

function getDefaultAuthenticatedRoute(userType) {
    switch (userType) {
        case 'landlord':
            return '/landlord/applications';
        case 'accountant':
            return '/payments';
        case 'internal_agent':
            return '/leads';
        default:
            return '/dashboard';
    }
}

// Maps Django user_type values to the six Brevo bot roles.
function brevoRoleForUserType(userType) {
    switch (userType) {
        case 'internal_agent':
            return 'internal_agent';
        case 'agent':
            return 'agent';
        case 'landlord':
        case 'accountant':
        case 'property_management':
            return 'pm';
        default:
            return 'pm'; // safe fallback for authed users; bot handles 'pm' branch
    }
}

function consumeAdminAccessTokenFromHash() {
    if (typeof window === 'undefined') return null;
    const hash = window.location.hash || '';
    if (!hash.startsWith('#')) return null;

    const params = new URLSearchParams(hash.slice(1));
    const token = params.get('adminAccessToken');
    if (!token) return null;

    storeCookiesInParentDomain({ name: ACCESS_TOKEN_COOKIE, value: token });
    params.delete('adminAccessToken');
    const nextHash = params.toString();
    const nextUrl = `${window.location.pathname}${window.location.search}${nextHash ? `#${nextHash}` : ''}`;
    window.history.replaceState(null, document.title, nextUrl);
    return token;
}

export const UserContext = createContext();

export const UserProvider = ({ children }) => {
    const location = useLocation();
    const navigate = useNavigate();

    const [user, setUser] = useState(null);
    const accessTokenRef = useRef(
        consumeAdminAccessTokenFromHash() || Cookies.get(ACCESS_TOKEN_COOKIE)
    );
    const intervalRef = useRef(null);
    const refreshPromiseRef = useRef(null);

    const setTokens = (access) => {
        accessTokenRef.current = access;
        if (access) {
            storeCookiesInParentDomain({ name: ACCESS_TOKEN_COOKIE, value: access });
        } else {
            removeCookieFromParentDomain(ACCESS_TOKEN_COOKIE);
        }
        // Refresh token is managed as an HttpOnly cookie by the server.
        // These are cleanup of pre-migration localStorage keys only.
        removeLocalStorageItem('accessToken');
        removeLocalStorageItem('refreshToken');
    };

    const resetPassword = useCallback(async ({ password, newPassword, confirmPassword }) => {
        try {
            const response = await fetch('/api/user/reset_password/', {
                method: 'PUT',
                headers: {
                    Authorization: `Bearer ${accessTokenRef.current}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ password, newPassword, confirmPassword }),
            });

            if (!response.ok) {
                response
                    .json()
                    .then((errorData) => {
                        console.log(errorData);
                        // Loop through each field in the error object
                        Object.keys(errorData).forEach((field) => {
                            // Loop through each error message for the current field
                            if (typeof errorData[field] === 'string') {
                                toast.error(errorData[field]);
                            } else if (Array.isArray(errorData[field])) {
                                errorData[field].forEach((message) => {
                                    toast.error(message);
                                });
                            }
                        });
                    })
                    .catch((error) => {
                        console.log(error);
                        toast.error(
                            apiErrorMessage(error, 'We could not parse the server response.')
                        );
                    });
                throw new Error('Registration failed');
            }

            const data = await response.json();
            setTokens(data.access);
            toast.success('Password reset successfully!');
        } catch (error) {
            return false;
        }
    }, []);

    const register = useCallback(async (email, password, name, company, phone) => {
        try {
            const response = await fetch('/api/user/registration/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, password, name, company, phone, username: 'lorem' }), // autogenerating username in the backend
            });

            if (!response.ok) {
                response
                    .json()
                    .then((errorData) => {
                        // Loop through each field in the error object
                        Object.keys(errorData).forEach((field) => {
                            // Loop through each error message for the current field
                            errorData[field].forEach((message) => {
                                toast.error(message);
                            });
                        });
                    })
                    .catch((error) => {
                        console.log(error);
                        toast.error(
                            apiErrorMessage(error, 'We could not parse the server response.')
                        );
                    });
                throw new Error('Registration failed');
            }

            const data = await response.json();
            setUser(data);
            setTokens(data.access);

            navigate('/onboarding');
        } catch (error) {
            return false;
        }
    }, []);

    const getAgentUrl = () => getAgentDashboardLink();

    const login = useCallback(
        async (username, password) => {
            try {
                const queryParams = new URLSearchParams(window.location.search);
                const requestedNextPage = queryParams.get('nextPage') || queryParams.get('next');
                const storedNextPage = getLocalStorageItem('nextPage') || requestedNextPage;
                const response = await fetch('/api/user/token/', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ username, password }),
                });

                if (!response.ok) throw new Error('Login failed');

                const data = await response.json();
                // Stores the access token as a leasehub.app-scoped cookie so
                // agent_dashboard/sign_dashboard (which have no login of
                // their own) can read the same session.
                setTokens(data.access);
                if (data.userType === 'agent') {
                    const agentURL = getAgentUrl();
                    const agentRedirectTarget = getSafeRedirectTarget(storedNextPage, {
                        allowAgentDashboard: true,
                    });
                    if (agentRedirectTarget) {
                        // Clear the stored nextPage to prevent future redirects
                        removeLocalStorageItem('nextPage');
                        window.location.href = agentRedirectTarget;
                        return true;
                    } else {
                        removeLocalStorageItem('nextPage');
                        window.location.href = agentURL;
                    }
                    return true;
                }

                setUser(data);
                capturePostHogEvent('login_succeeded', {
                    user_type: data.userType,
                    client_id: data.client_id,
                    client_name: data.client_name,
                });

                if (
                    data.clientType === null &&
                    data.isGuarantorSignatureRequired === null &&
                    data.isDepositLinkPresent === null &&
                    data.isHoldingDepositRequired === null &&
                    data.autoconfirmFeatureEnabled === null &&
                    data.isDefaultLockboxEnabled === null
                ) {
                    navigate('/onboarding');
                    return true;
                }

                // Check if there's a stored nextPage to redirect to
                if (storedNextPage && isSafeRedirectPath(storedNextPage)) {
                    // Clear the stored nextPage to prevent future redirects
                    removeLocalStorageItem('nextPage');
                    navigate(storedNextPage);
                    return true;
                } else if (storedNextPage) {
                    removeLocalStorageItem('nextPage');
                }

                navigate(getDefaultAuthenticatedRoute(data.userType));
                return true;
            } catch (error) {
                toast.error('Login failed. Please check your credentials.');
                return false;
            }
        },
        [navigate]
    );

    const logout = useCallback(async () => {
        try {
            await fetch('/api/user/logout/', { method: 'POST', credentials: 'include' });
        } catch (_) {
            // Best-effort: clear locally even if the server is unreachable.
        }
        capturePostHogEvent('logout_clicked');
        setUser(null);
        setTokens(null);
        resetPostHog();
        navigate('/login');
    }, [navigate]);

    const refreshTokens = useCallback(async () => {

        if (refreshPromiseRef.current) return refreshPromiseRef.current;

        refreshPromiseRef.current = (async () => {
            try {
                // The refresh token lives in an HttpOnly cookie; send it via credentials.
                const response = await fetch('/api/user/token/refresh/', {
                    method: 'POST',
                    credentials: 'include',
                });

                if (response.status === 401) {
                    logout();
                    return null;
                }
                if (!response.ok) {
                    return null;
                }

                const data = await response.json();

                if (!data?.access) {
                    logout();
                    return null;
                }
                setTokens(data.access);
                return data.access;
            } catch (error) {
                toast.error(`Refresh token error: ${error}`);
                return null;
            } finally {
                refreshPromiseRef.current = null;
            }
        })();

        return refreshPromiseRef.current;
    }, [logout]);

    const verifyToken = useCallback(async () => {
        const queryParams = new URLSearchParams(window.location.search);
        const nextPage = queryParams.get('nextPage');
        // Store nextPage in localStorage if on login page
        if (location.pathname === '/login' || location.pathname === '/login/') {
            if (nextPage) {
                setLocalStorageItem('nextPage', nextPage);
            }
        }

        if (!accessTokenRef.current) {
            navigate('/login');
            return;
        }

        try {
            const response = await fetch('/api/user/profile_details/', {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessTokenRef.current}`,
                },
            });

            if (response.ok) {
                const userData = await response.json();
                setUser(userData);
                if (userData?.userType === 'agent') {
                    console.log('Redirecting to agent dashboard');
                    console.log(nextPage);
                    const agentRedirectTarget = getSafeRedirectTarget(nextPage, {
                        allowAgentDashboard: true,
                    });
                    if (agentRedirectTarget && location.pathname === '/login') {
                        removeLocalStorageItem('nextPage');
                        window.location.href = agentRedirectTarget;
                    } else {
                        removeLocalStorageItem('nextPage');
                        window.location.href = getAgentUrl();
                    }
                }

                // check if user completed onboarding
                if (
                    userData?.clientType === null &&
                    userData?.isGuarantorSignatureRequired === null &&
                    userData?.isDepositLinkPresent === null &&
                    userData?.isHoldingDepositRequired === null &&
                    userData?.autoconfirmFeatureEnabled === null &&
                    userData?.isDefaultLockboxEnabled === null
                ) {
                    navigate('/onboarding');
                    return;
                }

                if (location.pathname === '/login' || location.pathname === '/onboarding') {
                    if (nextPage && isSafeRedirectPath(nextPage)) {
                        removeLocalStorageItem('nextPage');
                        navigate(nextPage);
                    } else {
                        if (nextPage) removeLocalStorageItem('nextPage');
                        navigate(getDefaultAuthenticatedRoute(userData.userType));
                    }
                }
            } else if (response.status === 401) {
                logout();
            } else {
                const error = new Error('Request failed');
                error.status = response.status;
                error.response = { status: response.status, data: null };
                toast.error(
                    apiErrorMessage(
                        error,
                        "We couldn't load your account right now. Please try again in a moment."
                    ),
                    {
                        toastId: `api-load-error:GET:${response.status}:background`,
                    }
                );
                logout();
            }
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Could not verify your session.'), {
                toastId: 'auth-verify-error',
            });
        }
    }, [location.pathname, navigate, logout]);

    useEffect(() => {
        let isMounted = true;
        if (accessTokenRef.current && isMounted) {
            verifyToken();
        }
        return () => {
            isMounted = false;
        };
    }, [verifyToken]);

    useEffect(() => {
        if (user) {
            Sentry.setUser({
                id: String(user?.user_id ?? user?.id ?? user?.email ?? ''),
                email: user?.email,
                username: user?.name ?? user?.username,
            });
            // Tags are indexed in Sentry — filter the Issues view by role /
            // client / company in one click.
            if (user?.userType) Sentry.setTag('user_role', String(user.userType));
            if (user?.client_id) Sentry.setTag('client_id', String(user.client_id));
            if (user?.client_name) Sentry.setTag('client_name', String(user.client_name));
            Sentry.setContext('user_profile', {
                user_id: user?.user_id,
                user_type: user?.userType,
                client_id: user?.client_id,
                client_name: user?.client_name,
                domain_name: user?.domain_name,
            });

            // Brevo: tag the visitor so the bot can branch on user_role.
            setBrevoUserRole(brevoRoleForUserType(user?.userType), {
                logged_in: true,
                user_type: user?.userType,
                user_id: user?.user_id ?? user?.id,
                client_id: user?.client_id,
                client_name: user?.client_name,
                name: user?.name,
                email: user?.email,
            });

            identifyPostHogUser(user?.user_id ?? user?.id ?? user?.email, {
                email: user?.email,
                name: user?.name ?? user?.username,
                user_type: user?.userType,
                client_id: user?.client_id,
                client_name: user?.client_name,
                domain_name: user?.domain_name,
            });
            groupPostHogClient(user?.client_id, {
                client_id: user?.client_id,
                client_name: user?.client_name,
                domain_name: user?.domain_name,
            });
        } else {
            Sentry.setUser(null);
            Sentry.setTag('user_role', undefined);
            Sentry.setTag('client_id', undefined);
            Sentry.setTag('client_name', undefined);
            resetPostHog();
            // Don't unset Brevo role on logout — public-flow pages may have
            // set a role (applicant/tenant/guarantor) that should persist.
            setBrevoContext({ logged_in: false });
        }
    }, [user]);

    useEffect(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }

        if (user) {
            // Proactively refresh 5 min before the 1-hour access token expires.
            intervalRef.current = setInterval(
                () => {
                    refreshTokens();
                },
                1000 * 60 * 30
            );
        }

        return () => clearInterval(intervalRef.current);
    }, [user, refreshTokens]);

    const value = useMemo(
        () => ({
            user,
            accessToken: accessTokenRef.current,
            accessTokenRef, // stable ref; lets imperative callers (apiContext) read the live token
            login,
            refreshTokens,
            verifyToken,
            logout,
            register,
            resetPassword,
        }),
        [user, login, refreshTokens, verifyToken, logout, register, resetPassword]
    );

    return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
